import { NavLink, useParams } from 'react-router-dom'

import {
  IconCard,
  IconFlag,
  IconLeaderboard,
  IconPokerChip,
  IconTarget,
  IconTrophy,
  IconUsers,
} from './Icon'

/**
 * Phase-aware bottom tab bar. Golf phase keeps the legacy layout. Poker phase swaps "Spill"
 * for "Bordet" (the poker seat view) and renames "Topp" → "Leaderboard".
 */
export type Phase = 'GOLF' | 'POKER'

type TabDef = {
  to: string
  label: string
  Icon: (p: { size?: number; className?: string }) => React.ReactElement
}

const GOLF_TABS = (k: string): TabDef[] => [
  { to: `/s/${k}/score`, label: 'Score', Icon: IconFlag },
  { to: `/s/${k}/scorecard`, label: 'Scorekort', Icon: IconCard },
  { to: `/s/${k}/leaderboard`, label: 'Leaderboard', Icon: IconLeaderboard },
  { to: `/s/${k}/sidegames`, label: 'Sidespill', Icon: IconTarget },
  { to: `/s/${k}/profile`, label: 'Profil', Icon: IconUsers },
]

// POKER phase commits to a lean rail — Bordet (your situation), Leaderboard (the
// field), Profil (you + round archive). Kort and Sidespill drop off; they're
// addressable from Profil's "Min runde" section. The thinking: golf is what the
// user DROVE all day; poker is what the app drives at them.
//
// "Premier" is added only when the event actually has payouts set — core
// "what am I playing for" info that's reachable in every poker state. Absent
// payouts, the tab is hidden entirely (not an empty page).
const POKER_TABS = (k: string, showPremier: boolean): TabDef[] => [
  { to: `/s/${k}/poker`, label: 'Forside', Icon: IconPokerChip },
  { to: `/s/${k}/stack`, label: 'Leaderboard', Icon: IconLeaderboard },
  ...(showPremier
    ? [{ to: `/s/${k}/payouts`, label: 'Premier', Icon: IconTrophy }]
    : []),
  { to: `/s/${k}/profile`, label: 'Profil', Icon: IconUsers },
]

const BottomNav = ({
  phase,
  showPremier = false,
}: {
  phase: Phase
  showPremier?: boolean
}) => {
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  const tabs =
    phase === 'POKER'
      ? POKER_TABS(playerKey, showPremier)
      : GOLF_TABS(playerKey)

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-50 border-t border-ink-200"
      style={{
        // Glass background per design.md §5 — translucent + blur so
        // scrolled-under content fades into view rather than being
        // hard-clipped by an opaque rail.
        background: 'rgba(255, 255, 255, 0.94)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <ul className="flex h-bottom-nav">
        {tabs.map(({ to, label, Icon }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={false}
              className={({ isActive }) =>
                `relative h-full flex flex-col items-center justify-center gap-1 text-xs font-medium transition-colors ${
                  isActive ? 'text-green-800' : 'text-ink-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {/* The flag — 3×24 red bar pinned to the top edge of
                      the active tab. Not a pill, not a dot, a flag. */}
                  {isActive ? (
                    <span
                      aria-hidden
                      className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-[3px] rounded-b-sm bg-flag"
                    />
                  ) : null}
                  <Icon size={22} />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default BottomNav
